import { useRef, useState } from 'react';

import { getErrorMessage } from '@/api';
import { lookupNetworkApp } from '@/api/networkApps';
import { useToast } from '@/components/ui';

import { NETWORK_FIELD_SPECS, platformLabel } from './infoTabFields';
import type { NetworkFieldSpec, PlatformState } from './infoTabFields';

// 일괄 조회 진행 중을 나타내는 sentinel — 개별 네트워크 code와 겹치지 않는 값.
export const LOOKUP_ALL = '__all__';

// 네트워크 Management API 조회 — 프로젝트 상세(InfoTab)와 신규 생성(ProjectNew)이 함께 쓴다.
// 조회는 지금 보고 있는 플랫폼 하나만 대상으로 하고, 저장은 하지 않는다:
// 채워진 값은 각 화면의 기존 저장/요청 흐름(admin 즉시 반영 / 비-admin 승인 요청)을 그대로 탄다.
export function useNetworkLookup(
  platforms: Record<string, PlatformState>,
  setPlatformField: (code: string, key: keyof PlatformState, value: string) => void,
) {
  const toast = useToast();
  // 조회 중인 네트워크 code — null이면 대기 상태.
  const [lookingUpNetwork, setLookingUpNetwork] = useState<string | null>(null);
  // 응답이 돌아온 시점의 최신 store_id를 보기 위한 참조 (아래 staleStoreId 참고).
  const platformsRef = useRef(platforms);
  platformsRef.current = platforms;

  const storeIdOf = (platformCode: string) =>
    (platformsRef.current[platformCode]?.store_id ?? '').trim();

  // 조회 시작 전 store_id 확보. 비어 있으면 안내만 하고 조회하지 않는다.
  // (버튼이 비활성화되어 있어 정상 흐름에서는 오지 않는다 — 호출부 직접 사용 대비 방어.)
  const requireStoreId = (platformCode: string): string | null => {
    const storeId = storeIdOf(platformCode);
    if (!storeId) {
      toast.error(`${platformLabel(platformCode)} store_id를 먼저 입력해주세요`);
      return null;
    }
    return storeId;
  };

  // 조회 중에 사용자가 store_id를 고쳤으면 응답은 이미 다른 앱의 것이다 — 채우지 않는다.
  const staleStoreId = (platformCode: string, storeId: string): boolean => {
    if (storeIdOf(platformCode) === storeId) return false;
    toast.error(`${platformLabel(platformCode)} store_id가 조회 중에 바뀌어 결과를 버렸어요`);
    return true;
  };

  const lookupNetwork = async (spec: NetworkFieldSpec, platformCode: string) => {
    const storeId = requireStoreId(platformCode);
    if (!storeId) return;
    const label = platformLabel(platformCode);
    setLookingUpNetwork(spec.network);
    try {
      const result = await lookupNetworkApp(spec.network, platformCode, storeId);
      if (staleStoreId(platformCode, storeId)) return;
      const match = result.match;
      if (!match) {
        toast.error(`${result.label}에 ${label} store_id(${storeId})와 맞는 앱이 없어요`);
        return;
      }
      setPlatformField(platformCode, spec.field, match.network_app_id);
      toast.success(`${label} ${spec.field} = ${match.network_app_id} (${match.name ?? '이름 없음'})`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLookingUpNetwork(null);
    }
  };

  // 일괄 조회 — 지금 보고 있는 플랫폼의 store_id 하나로 지원 네트워크를 모두 조회한다.
  // 지원 네트워크 모두 매칭 키가 store_id라 가능한 동작이다. DT/Unity/Vungle은 서버가 앱 목록을
  // 캐시하고 Tapjoy는 검색 1회라, 호출 비용은 사실상 없다. 하나가 실패해도 나머지는 채운다.
  const lookupAllNetworks = async (platformCode: string) => {
    const storeId = requireStoreId(platformCode);
    if (!storeId) return;
    const label = platformLabel(platformCode);
    setLookingUpNetwork(LOOKUP_ALL);
    try {
      const results = await Promise.allSettled(
        NETWORK_FIELD_SPECS.map((spec) => lookupNetworkApp(spec.network, platformCode, storeId)),
      );
      if (staleStoreId(platformCode, storeId)) return;
      const filled: string[] = [];
      const missed: string[] = [];
      const failed: string[] = [];
      results.forEach((result, index) => {
        const spec = NETWORK_FIELD_SPECS[index];
        if (result.status === 'rejected') {
          failed.push(spec.label);
          return;
        }
        const match = result.value.match;
        if (!match) {
          missed.push(spec.label);
          return;
        }
        setPlatformField(platformCode, spec.field, match.network_app_id);
        filled.push(spec.label);
      });

      const parts = [`${label} ${filled.length}/${NETWORK_FIELD_SPECS.length} 채웠어요`];
      if (missed.length > 0) parts.push(`미매칭: ${missed.join(', ')}`);
      if (failed.length > 0) parts.push(`조회 실패: ${failed.join(', ')}`);
      const message = parts.join(' · ');
      if (filled.length === 0) toast.error(message);
      else toast.success(message);
    } finally {
      setLookingUpNetwork(null);
    }
  };

  return { lookingUpNetwork, lookupNetwork, lookupAllNetworks };
}
